// Načítání distinct hodnot pro select dropdowny v ClientDetail edit režimu (Modul 2B).
//
// Místo hardcodovaného listu balíčků/trenérů/lokalit načteme reálně používané hodnoty
// z /api/clients. Výhoda: nový balíček v Notion DB Klienti se v dropdownu objeví automaticky,
// jakmile někdo má klienta s tímto balíčkem (= v praxi okamžitě po prvním použití).
//
// Cache: module-level, TTL 5 min. Bez TTL by user musel reload stránky pro nové options.
// Plus per-field utility addCurrentToOptions — pro safety vždy přidá aktuální hodnotu klienta
// do listu (i kdyby v cache nebyla, např. cache outdated).

import { apiFetch } from "./api.js";

const TTL_MS = 5 * 60 * 1000;
let cache = null;          // { balicek: [...], trener: [...], lokalita: [...], typSpoluprace: [...], zpusobPlatby: [...] }
let cacheTime = 0;
let inflight = null;       // sdílený Promise pro deduplikaci paralelních volání

// Pole klient objektu která chceme jako select options.
// Klíč v cache = klíč v body.fields v /api/klient-edit (musí matchovat FIELD_CONFIG na backendu).
const FIELDS = [
  "balicek", "trener", "lokalita", "typSpoluprace", "zpusobPlatby", "frekvenceFakturace", "kdoFakturuje",
  // Modul 2B
  "stage", "stav", "blacklist",
  // Modul 2D — Vstupní dotazník (z poptávky FAPI form)
  "levelTreninku", "pocetTreninkuTyden", "casProTreninky", "zajemOLokalitu", "zajemTrial", "zkusenostiCviceni",
  "kdyChceZacit", "delkaSpoluprace",
];

function _collect(clients, key) {
  const set = new Set();
  for (const c of clients || []) {
    const v = c?.[key];
    if (v !== undefined && v !== null && String(v).trim() !== "") {
      set.add(String(v).trim());
    }
  }
  // Sort case-insensitive (cs locale aby diakritika seřadila správně)
  return Array.from(set).sort((a, b) => a.localeCompare(b, "cs", { sensitivity: "base" }));
}

// Vrátí cached options nebo načte z /api/clients. Sdílí inflight Promise mezi paralelními volání.
export async function getClientFieldOptions() {
  if (cache && Date.now() - cacheTime < TTL_MS) return cache;
  if (inflight) return inflight;

  inflight = (async () => {
    try {
      const res = await apiFetch("/api/clients");
      const data = await res.json();
      const clients = Array.isArray(data) ? data : (data.clients || data.data || []);
      const next = {};
      for (const f of FIELDS) next[f] = _collect(clients, f);
      cache = next;
      cacheTime = Date.now();
      return cache;
    } catch (e) {
      // Při chybě vrátit prázdné options — frontend ukáže fallback (jen current value)
      console.warn("[clientFieldOptions] fetch failed:", e);
      cache = Object.fromEntries(FIELDS.map((f) => [f, []]));
      cacheTime = Date.now();
      return cache;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

// Přidá currentValue na začátek options (pokud tam ještě není). Bezpečnost — pokud
// klient má hodnotu která v distinct cache není (cache outdated), select ji neztratí.
export function addCurrentToOptions(options, currentValue) {
  const cur = (currentValue || "").trim();
  const list = Array.isArray(options) ? options : [];
  if (!cur) return list;
  if (list.includes(cur)) return list;
  return [cur, ...list];
}

// Force refresh cache (volat po klient-edit aby nové hodnoty se objevily v dalších select).
export function invalidateClientFieldOptions() {
  cache = null;
  cacheTime = 0;
}
